import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { GatewayProvider, GatewayDest } from 'react-gateway';

import { postPageResized } from '../../../post-page/actions/post-page-resize';
import { isEditor, getViewMode } from '../../selectors/view-mode-selectors';
import WindowResizeListener from '../window-resize-listener';
import AppWrapper from '../app-wrapper';
import MessageRoot from '../../containers/message-root';
import DemoMessageRoot from '../../containers/demo-message-root';
import ModalLoader from '../../containers/modal-loader';
import AccessibilityListener from '../accessibility-listener';
import { isInWix } from '../../services/is-in-wix';
import { isPostPageBundle } from '../../services/section-route-utils';
import { EXPERIMENT_POST_PAGE_MIGRATION, EXPERIMENT_RESPONSIVE_LAYOUT } from '@wix/communities-blog-experiments';
import { POST_PAGE_MIN_WIDTH } from '../../constants/post-page';
import PostPageMigrationMessage from '../../containers/post-page-migration-message';
import Wix from '../../services/wix-sdk-polyfill';
import withBlogMenuSettings from '../../hoc/with-blog-menu-settings';
import withExperiment from '../../hoc/with-experiment';
import UpdateUrlsNotification from '../../../content-migration/components/update-urls-notification';
import styles from './app.scss';

const MIN_WIDTH = 980;

export class AppDesktop extends Component {
  componentDidMount() {
    if (!isInWix()) {
      document.documentElement.classList.add('enable-scroll');
    }

    this.appWidth = window.innerWidth;
  }

  handleResize = () => {
    const { isPostPageBundle, isResponsiveLayoutExperimentEnabled, postPageResized, viewMode } = this.props;

    const minWidth = isPostPageBundle ? POST_PAGE_MIN_WIDTH : MIN_WIDTH;
    let width = window.innerWidth;

    if (width % 2) {
      width = width - 1;
    }

    if (!isResponsiveLayoutExperimentEnabled && width < minWidth) {
      width = minWidth;
    }

    if (window.innerWidth !== width) {
      Wix.resizeComponent({
        width,
        height: window.innerHeight,
      });
    }

    if (isPostPageBundle && this.appWidth !== width) {
      postPageResized({ viewMode, width, buildertype: 'desktop' });
      this.appWidth = width;
    }
  };

  render() {
    const {
      showBlogMenu,
      desktopHeader,
      menu,
      main,
      children,
      className,
      isEditor,
      isPostPageMigrationExperimentEnabled,
      isPostPageBundle,
      isResponsiveLayoutExperimentEnabled,
    } = this.props;
    return (
      <GatewayProvider>
        <AppWrapper
          className={classNames(
            className,
            styles.app,
            'blog-background-color',
            isPostPageBundle && styles.postPageApp,
            isPostPageBundle && !isResponsiveLayoutExperimentEnabled && styles.fixed,
          )}
        >
          {isEditor && <WindowResizeListener onResize={this.handleResize} />}
          <AccessibilityListener />
          <MessageRoot />
          <ModalLoader />
          {isPostPageMigrationExperimentEnabled && <PostPageMigrationMessage />}
          <DemoMessageRoot />
          <div id="content-wrapper">
            {isEditor && <UpdateUrlsNotification />}
            {showBlogMenu && desktopHeader && React.cloneElement(desktopHeader, { menu })}
            {main && React.cloneElement(main, { children })}
          </div>
          <GatewayDest name="footer" />
          <GatewayDest name="floating-action-button" />
          <GatewayDest name="tooltip" />
        </AppWrapper>
      </GatewayProvider>
    );
  }
}

AppDesktop.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  desktopHeader: PropTypes.node,
  isEditor: PropTypes.bool,
  isPostPageBundle: PropTypes.bool,
  isPostPageMigrationExperimentEnabled: PropTypes.bool,
  isResponsiveLayoutExperimentEnabled: PropTypes.bool,
  main: PropTypes.node.isRequired,
  menu: PropTypes.node,
  postPageResized: PropTypes.func,
  showBlogMenu: PropTypes.bool,
  viewMode: PropTypes.string,
};

const mapStateToProps = state => ({
  isEditor: isEditor(state),
  isPostPageBundle: isPostPageBundle(),
  viewMode: getViewMode(state),
});

const mapDispatchToProps = {
  postPageResized,
};

export default flowRight(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withBlogMenuSettings,
  withExperiment({
    isPostPageMigrationExperimentEnabled: EXPERIMENT_POST_PAGE_MIGRATION,
    isResponsiveLayoutExperimentEnabled: EXPERIMENT_RESPONSIVE_LAYOUT,
  }),
)(AppDesktop);
